import { LOAI_DINH_MUC } from "@/core/constants/const";
import { swalNotification } from "@/core/helpers/utils";
import Swal from "sweetalert2";
import type { Ref } from "vue";
import { resolveNgoaiDinhMucExportMax } from "../constants";
import type { IPhieuXuatKhoSuppliesQuantityRow } from "../types";

export function useSuppliesQuantityValidation(
  loaiDinhMuc: Ref<LOAI_DINH_MUC | undefined>,
  getConThieu: (idVatTu: number) => number
) {
  const recalcTaiSd = (row: IPhieuXuatKhoSuppliesQuantityRow) => {
    row.tai_sd = row.so_luong_se_lay - row.mat - row.so_luong_phe_lieu;

    if (row.tai_sd < 0) {
      row.tai_sd = 0;
    }
  };

  const onMatChanged = (
    row: IPhieuXuatKhoSuppliesQuantityRow,
    value: unknown
  ) => {
    const numericValue = typeof value === "number" ? value : row.mat;

    if (numericValue < 0) {
      row.mat = 0;
    }

    if (row.mat > row.so_luong_se_lay) {
      swalNotification("Số lượng mất không thể vượt quá SL Xuất", "error");
      row.mat = row.so_luong_se_lay;
    }

    if (row.mat + row.so_luong_phe_lieu > row.so_luong_se_lay) {
      swalNotification(
        "Tổng Mất + Phế liệu không thể vượt quá SL Xuất",
        "error"
      );
      row.mat = row.so_luong_se_lay - row.so_luong_phe_lieu;

      if (row.mat < 0) {
        row.mat = 0;
      }
    }

    recalcTaiSd(row);
  };

  const onSoLuongPheLieuChanged = (
    row: IPhieuXuatKhoSuppliesQuantityRow,
    value: unknown
  ) => {
    const numericValue =
      typeof value === "number" ? value : row.so_luong_phe_lieu;

    if (numericValue < 0) {
      row.so_luong_phe_lieu = 0;
    }

    if (row.mat + row.so_luong_phe_lieu > row.so_luong_se_lay) {
      swalNotification(
        "Tổng Mất + Phế liệu không thể vượt quá SL Xuất",
        "error"
      );
      row.so_luong_phe_lieu = row.so_luong_se_lay - row.mat;

      if (row.so_luong_phe_lieu < 0) {
        row.so_luong_phe_lieu = 0;
      }
    }

    if (row.so_luong_phe_lieu === 0) {
      row.quy_doi_kg = 0;
    }

    recalcTaiSd(row);
  };

  const onQuyDoiKgChanged = (
    row: IPhieuXuatKhoSuppliesQuantityRow,
    value: unknown
  ) => {
    const numericValue = typeof value === "number" ? value : row.quy_doi_kg;

    if (numericValue < 0) {
      row.quy_doi_kg = 0;
    }

    if (row.so_luong_phe_lieu > 0 && row.quy_doi_kg === 0) {
      swalNotification(
        "Quy đổi (Kg) phải khác 0 khi Số lượng phế liệu > 0",
        "error"
      );
    }
  };

  const toExportQuantity = (
    value: unknown,
    fallback: number | null | undefined
  ): number => {
    const raw = typeof value === "number" ? value : fallback;
    const numericValue = Number(raw);

    if (raw == null || Number.isNaN(numericValue)) {
      return 0;
    }

    return numericValue;
  };

  const onExportQuantityChanged = async (
    row: IPhieuXuatKhoSuppliesQuantityRow,
    value: unknown,
    previousValue = row.so_luong_se_lay
  ) => {
    const numericValue = toExportQuantity(value, row.so_luong_se_lay);
    const loaiDm = loaiDinhMuc.value;
    const chenhLech = row.chenh_lech ?? getConThieu(row.id_vat_tu);
    const safePrevious =
      previousValue == null || Number.isNaN(Number(previousValue))
        ? 0
        : Number(previousValue);

    if (numericValue < 0) {
      swalNotification("Số lượng vật tư lấy phải dương", "error");
      row.so_luong_se_lay = 0;
      recalcTaiSd(row);
      return;
    }

    if (numericValue > row.so_luong_trong_kho_ban_dau) {
      await swalNotification(
        "Không thể lấy nhiều hơn số lượng vật tư có sẵn trong kho",
        "error"
      );
      row.so_luong_se_lay = Math.min(
        row.so_luong_trong_kho_ban_dau,
        safePrevious
      );
      recalcTaiSd(row);
      return;
    }

    if (loaiDm === LOAI_DINH_MUC.DINH_MUC_CO_DINH && numericValue > chenhLech) {
      const { isConfirmed } = await Swal.fire({
        title: "Chú ý",
        text: "Số lượng vật tư vượt quá số lượng trong định mức, bạn có chắc chắn muốn tiếp tục?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Đồng ý",
        cancelButtonText: "Huỷ",
      });

      if (!isConfirmed) {
        row.so_luong_se_lay = safePrevious;
        recalcTaiSd(row);
        return;
      }
    } else if (loaiDm === LOAI_DINH_MUC.NGOAI_DINH_MUC) {
      const maxExport = resolveNgoaiDinhMucExportMax(
        row,
        getConThieu(row.id_vat_tu)
      );

      if (numericValue > maxExport) {
        await swalNotification(
          "Không thể lấy quá số lượng chênh lệch ngoài định mức",
          "error"
        );
        row.so_luong_se_lay = Math.min(maxExport, safePrevious);
        recalcTaiSd(row);
        return;
      }
    }

    row.so_luong_se_lay = numericValue;
    recalcTaiSd(row);
  };

  const onManualExportQuantityChanged = async (
    row: IPhieuXuatKhoSuppliesQuantityRow,
    value: unknown
  ) => {
    row.so_luong_se_lay = toExportQuantity(value, row.so_luong_se_lay);

    if (row.so_luong_se_lay < 0) {
      swalNotification("Số lượng vật tư xuất phải dương", "error");
      row.so_luong_se_lay = 0;
    }

    if (row.so_luong_se_lay > row.so_luong_trong_kho_ban_dau) {
      await swalNotification(
        "Không thể xuất nhiều hơn số lượng vật tư có sẵn trong kho",
        "error"
      );
      row.so_luong_se_lay = row.so_luong_trong_kho_ban_dau;
    }

    if (row.so_luong_se_lay <= 0) {
      row.so_luong_se_lay = 0;
    }

    recalcTaiSd(row);
  };

  const validateThuHoiRows = (
    rows: IPhieuXuatKhoSuppliesQuantityRow[]
  ): boolean => {
    const invalidRow = rows.find(
      (item) => item.so_luong_phe_lieu > 0 && item.quy_doi_kg === 0
    );

    if (invalidRow) {
      swalNotification(
        "Quy đổi (Kg) phải khác 0 khi Số lượng phế liệu > 0",
        "error"
      );

      return false;
    }

    return true;
  };

  const validateNgoaiDinhMucExportRows = (
    rows: IPhieuXuatKhoSuppliesQuantityRow[]
  ): boolean => {
    if (loaiDinhMuc.value !== LOAI_DINH_MUC.NGOAI_DINH_MUC) {
      return true;
    }

    const invalidRow = rows.find((row) => {
      if (Number(row.so_luong_se_lay) <= 0) {
        return false;
      }

      const maxExport = resolveNgoaiDinhMucExportMax(
        row,
        getConThieu(row.id_vat_tu)
      );

      return Number(row.so_luong_se_lay) > maxExport;
    });

    if (invalidRow) {
      swalNotification(
        "Không thể lấy quá số lượng chênh lệch ngoài định mức",
        "error"
      );

      return false;
    }

    return true;
  };

  return {
    onExportQuantityChanged,
    onManualExportQuantityChanged,
    onMatChanged,
    onQuyDoiKgChanged,
    onSoLuongPheLieuChanged,
    recalcTaiSd,
    validateNgoaiDinhMucExportRows,
    validateThuHoiRows,
  };
}
